/**
 * Digilog cart sync — theme-side integration.
 *
 * Hooks the theme's own `cart:update` event (fired after every successful
 * add/change/update/remove, from the drawer, cart page, and quick-add alike)
 * instead of intercepting fetch calls — so this works for every mutation
 * path the theme already has, with nothing to keep in sync if the theme
 * adds a new one later.
 *
 * Requires a tiny Liquid snippet to set window.__cartSync before this file
 * loads (see snippets/cart-sync-init.liquid): customer id (or null) and the
 * shop domain. No tokens or secrets live in this file — identity is proven
 * server-side via Shopify's signed app proxy request, not by anything sent
 * from here.
 */
(function () {
  "use strict";

  var config = window.__cartSync || {};
  var customerId = config.customerId || null;
  var PROXY_BASE = "/apps/cart-sync";
  var SYNC_DEBOUNCE_MS = 1200;
  var GUEST_CART_KEY = "digilog_guest_cart_pending_merge";

  if (!customerId) {
    // Signed-out visitor (includes just-logged-out, since logout is a real
    // navigation to this same template). Nothing to restore or push — App
    // Proxy would reject a guest request anyway. Also sweep any leftover
    // per-customer flags from this browser's session storage so a shared
    // computer can't carry state from whoever was signed in before into
    // whoever opens the site next.
    try {
      var keysToRemove = [];
      for (var i = 0; i < window.sessionStorage.length; i++) {
        var k = window.sessionStorage.key(i);
        if (k && k.indexOf("digilog_cart_sync_restored_") === 0) keysToRemove.push(k);
      }
      keysToRemove.forEach(function (k) {
        window.sessionStorage.removeItem(k);
      });
    } catch (e) {
      // sessionStorage can throw in locked-down/private contexts; nothing
      // sensitive is at stake either way since no cart content is cached
      // here, so just move on.
    }
    initGuestLoginBanner();
    return;
  }

  var state = {
    revision: null, // null = haven't fetched the server's version yet
    syncTimer: null,
    syncInFlight: false,
    pendingResync: false,
    applyingRemoteCart: false, // guards against our own restore re-triggering a save
  };

  function proxyFetch(path, options) {
    options = options || {};
    options.credentials = "same-origin"; // same-origin so Shopify's edge attaches the signed params
    return fetch(PROXY_BASE + path, options);
  }

  function toServerItems(shopifyCartItems) {
    return (shopifyCartItems || []).map(function (item) {
      return {
        variant_id: item.variant_id || item.id,
        quantity: item.quantity,
        properties: item.properties && Object.keys(item.properties).length ? item.properties : null,
        selling_plan_id: item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.id : null,
      };
    });
  }

  function pushCartToServer(shopifyCartItems) {
    if (state.revision === null) {
      // Haven't established a base revision yet (restore-on-login hasn't run
      // or hasn't finished) — queue a resync instead of writing blind.
      state.pendingResync = true;
      return;
    }
    if (state.syncInFlight) {
      state.pendingResync = true;
      return;
    }
    state.syncInFlight = true;

    proxyFetch("/cart", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        base_revision: state.revision,
        items: toServerItems(shopifyCartItems),
        action: "sync",
      }),
    })
      .then(function (res) {
        return res.json().then(function (body) {
          return { status: res.status, body: body };
        });
      })
      .then(function (result) {
        state.syncInFlight = false;
        if (result.status === 200) {
          state.revision = result.body.revision;
        } else if (result.status === 409) {
          // Someone else (another device) wrote since our last read. Adopt
          // the server's current revision and retry with our latest local
          // cart rather than silently dropping the write.
          state.revision = result.body.current.revision;
          state.pendingResync = true;
        }
        if (state.pendingResync) {
          state.pendingResync = false;
          fetch("/cart.js")
            .then(function (r) {
              return r.json();
            })
            .then(function (cart) {
              pushCartToServer(cart.items);
            });
        }
      })
      .catch(function () {
        state.syncInFlight = false;
        // Network/server hiccup: normal shopping must keep working. The
        // next cart:update (or the next page's restore-on-login check)
        // will retry naturally; nothing here blocks checkout.
      });
  }

  function scheduleSync(shopifyCartItems) {
    if (state.applyingRemoteCart) return; // this change came FROM the server, don't echo it back
    window.clearTimeout(state.syncTimer);
    state.syncTimer = window.setTimeout(function () {
      pushCartToServer(shopifyCartItems);
    }, SYNC_DEBOUNCE_MS);
  }

  // cart:update's event.detail.cartData shape is inconsistent across
  // trigger paths — confirmed live that a plain product-page "Add to Cart"
  // button fires this event with cartData present but cartData.items
  // undefined, while cart-drawer-triggered updates do populate it. Trusting
  // the event payload risked syncing an empty item list and wiping a
  // customer's saved cart. Treat the event purely as a "something changed"
  // signal and always re-fetch the authoritative cart from /cart.js.
  document.addEventListener("cart:update", function () {
    fetch("/cart.js")
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        scheduleSync(cart.items);
      });
  });

  // ---- Restore-on-login / merge ---------------------------------------

  function localCart() {
    return fetch("/cart.js").then(function (r) {
      return r.json();
    });
  }

  // Adds one item at a time rather than one batched request. Shopify's
  // /cart/add.js rejects an entire batched call if any single line fails
  // (e.g. one sold-out variant), which would silently drop every other
  // saved item along with it. Going one at a time means a sold-out item
  // only costs that one item — everything still available still restores,
  // and we get a precise, nameable list of what didn't make it across,
  // instead of an all-or-nothing failure. This is also where price/stock
  // become authoritative: each add is evaluated by Shopify against live
  // data, never against anything saved from before.
  function addItemsToLocalCart(items) {
    var succeeded = [];
    var failed = [];

    function addOne(item) {
      return fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties || undefined,
          selling_plan: item.selling_plan_id || undefined,
        }),
      })
        .then(function (res) {
          return res.json().then(function (body) {
            return { res: res, body: body };
          });
        })
        .then(function (result) {
          if (result.res.ok) {
            succeeded.push(item);
          } else {
            failed.push({
              item: item,
              reason: result.body && result.body.description || result.body && result.body.message || "No longer available",
            });
          }
        })
        .catch(function () {
          failed.push({ item: item, reason: "Could not reach the store to check this item" });
        });
    }

    return items
      .reduce(function (chain, item) {
        return chain.then(function () {
          return addOne(item);
        });
      }, Promise.resolve())
      .then(function () {
        return { succeeded: succeeded, failed: failed };
      });
  }

  function showUnavailableNotice(failed) {
    if (!failed.length) return;
    var banner = document.createElement("div");
    banner.setAttribute("role", "status");
    banner.style.cssText =
      "position:fixed;bottom:16px;right:16px;max-width:360px;background:#fff;border:1px solid #d33;" +
      "padding:16px;border-radius:4px;z-index:9998;box-shadow:0 2px 12px rgba(0,0,0,.2);";
    var html = "<p style=\"margin:0 0 8px;font-weight:bold;\">" +
      (failed.length === 1 ? "1 saved item couldn't be restored:" : failed.length + " saved items couldn't be restored:") +
      "</p><ul style=\"margin:0 0 8px;padding-left:18px;\">";
    failed.forEach(function (f) {
      html += "<li>Variant " + f.item.variant_id + " — " + f.reason + "</li>";
    });
    html += "</ul>";
    banner.innerHTML = html;
    var closeBtn = document.createElement("button");
    closeBtn.textContent = "Dismiss";
    closeBtn.style.cssText = "padding:6px 10px;cursor:pointer;";
    closeBtn.addEventListener("click", function () {
      document.body.removeChild(banner);
    });
    banner.appendChild(closeBtn);
    document.body.appendChild(banner);
  }

  // --- Guest login nudge --------------------------------------------------

  var LOGIN_BANNER_DISMISSED_KEY = "digilog_cart_sync_login_banner_dismissed";

  function showLoginBanner() {
    if (document.getElementById("digilog-cart-sync-login-banner")) return; // already showing
    try {
      if (window.sessionStorage.getItem(LOGIN_BANNER_DISMISSED_KEY)) return;
    } catch (e) {}

    var banner = document.createElement("div");
    banner.id = "digilog-cart-sync-login-banner";
    banner.setAttribute("role", "status");
    banner.style.cssText =
      "position:fixed;bottom:16px;right:16px;max-width:320px;background:#fff;border:1px solid #ddd;" +
      "padding:14px 16px;border-radius:4px;z-index:9997;box-shadow:0 2px 12px rgba(0,0,0,.15);" +
      "display:flex;align-items:center;gap:10px;flex-wrap:wrap;";
    banner.innerHTML =
      "<span style=\"flex:1;min-width:180px;\">Sign in to save your cart and pick up where you left off on any device.</span>";

    var signInLink = document.createElement("a");
    signInLink.href = "/account/login";
    signInLink.textContent = "Sign in";
    signInLink.style.cssText =
      "padding:6px 12px;background:#000;color:#fff;text-decoration:none;border-radius:3px;white-space:nowrap;";
    banner.appendChild(signInLink);

    var closeBtn = document.createElement("button");
    closeBtn.textContent = "×";
    closeBtn.setAttribute("aria-label", "Dismiss");
    closeBtn.style.cssText = "background:none;border:none;font-size:18px;line-height:1;cursor:pointer;padding:0 4px;";
    closeBtn.addEventListener("click", function () {
      document.body.removeChild(banner);
      try {
        window.sessionStorage.setItem(LOGIN_BANNER_DISMISSED_KEY, "1");
      } catch (e) {}
    });
    banner.appendChild(closeBtn);

    document.body.appendChild(banner);
  }

  function initGuestLoginBanner() {
    try {
      if (window.sessionStorage.getItem(LOGIN_BANNER_DISMISSED_KEY)) return;
    } catch (e) {}

    // Show now if the guest already has something in their cart (e.g. they
    // added an item, then navigated to a different page).
    fetch("/cart.js")
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (cart.item_count > 0) showLoginBanner();
      })
      .catch(function () {});

    // And show reactively the moment a guest adds their first item, without
    // needing a page navigation first. Same payload-shape issue as the main
    // sync listener above: cart:update's event.detail.cartData.items is
    // unreliable (confirmed undefined on a plain PDP add-to-cart), so this
    // re-fetches /cart.js instead of trusting the event detail.
    document.addEventListener("cart:update", function () {
      fetch("/cart.js")
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          if (cart.item_count > 0) showLoginBanner();
        })
        .catch(function () {});
    });
  }

  function replaceLocalCartWith(items) {
    // Used for "Use saved cart": clear what's there, then add the saved set.
    // /cart/clear.js empties the cart without deleting it.
    return fetch("/cart/clear.js", { method: "POST" }).then(function () {
      return addItemsToLocalCart(items);
    });
  }

  function refreshThemeCartUI() {
    document.dispatchEvent(new CustomEvent("cart:reload", { bubbles: true }));
  }

  function showMergePrompt(localItems, savedItems, onChoice) {
    var overlay = document.createElement("div");
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", "Saved cart found");
    overlay.style.cssText =
      "position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:9999;display:flex;align-items:center;justify-content:center;";

    var box = document.createElement("div");
    box.style.cssText =
      "background:#fff;max-width:420px;width:90%;padding:24px;border-radius:4px;font-family:inherit;";
    box.innerHTML =
      "<p style=\"margin:0 0 16px;\">You have a saved cart from another device, and items already in this cart. What would you like to do?</p>";

    function makeButton(label, choice) {
      var btn = document.createElement("button");
      btn.textContent = label;
      btn.style.cssText =
        "display:block;width:100%;margin-bottom:8px;padding:10px;cursor:pointer;";
      btn.addEventListener("click", function () {
        document.body.removeChild(overlay);
        onChoice(choice);
      });
      return btn;
    }

    box.appendChild(makeButton("Merge carts", "merge"));
    box.appendChild(makeButton("Use saved cart", "use_saved"));
    box.appendChild(makeButton("Keep this cart", "keep_local"));
    overlay.appendChild(box);
    document.body.appendChild(overlay);
  }

  function restoreOnLogin() {
    Promise.all([localCart(), proxyFetch("/cart").then(function (r) { return r.json(); })])
      .then(function (results) {
        var local = results[0];
        var saved = results[1];
        state.revision = saved.revision;

        if (!saved.items.length) {
          return; // nothing saved yet — current local cart is authoritative
        }

        if (!local.items.length) {
          // Empty local cart: restore automatically, no prompt needed.
          state.applyingRemoteCart = true;
          addItemsToLocalCart(saved.items)
            .then(function (result) {
              refreshThemeCartUI();
              showUnavailableNotice(result.failed);
            })
            .finally(function () {
              state.applyingRemoteCart = false;
            });
          return;
        }

        // Both have items: never silently pick one.
        showMergePrompt(local.items, saved.items, function (choice) {
          state.applyingRemoteCart = true;
          var action;
          if (choice === "use_saved") {
            action = replaceLocalCartWith(saved.items);
          } else if (choice === "merge") {
            action = addItemsToLocalCart(saved.items);
          } else {
            action = Promise.resolve({ succeeded: [], failed: [] }); // keep_local: push current cart as the saved one
          }
          action
            .then(function (result) {
              refreshThemeCartUI();
              showUnavailableNotice(result.failed);
            })
            .then(function () {
              return localCart();
            })
            .then(function (mergedCart) {
              return pushCartToServer(mergedCart.items);
            })
            .finally(function () {
              state.applyingRemoteCart = false;
            });
        });
      })
      .catch(function () {
        // Can't reach the saved cart right now — fail open: shopping
        // continues on whatever's already in the local cart.
      });
  }

  // Run once per sign-in, not on every page view, to avoid re-prompting a
  // customer who already made their merge choice this session.
  var sessionFlag = "digilog_cart_sync_restored_" + customerId;
  if (!window.sessionStorage.getItem(sessionFlag)) {
    window.sessionStorage.setItem(sessionFlag, "1");
    restoreOnLogin();
  } else {
    // Still need a revision to sync against even without re-prompting.
    proxyFetch("/cart")
      .then(function (r) {
        return r.json();
      })
      .then(function (saved) {
        state.revision = saved.revision;
      });
  }
})();
